import { useEffect } from 'react';
import { getActiveTransaction } from '@sentry/tracing';
import { RouteNames } from './routes';

type SpanData = Record<string, string | number | boolean>;

/** Minimal auth user shape for bootstrap gate checks (avoids importing AuthStore). */
export type AuthBootstrapUser =
  | {
      canUseApp?: boolean;
      hasOrganization?: boolean;
      hasPendingInvitation?: boolean;
      isGuest?: boolean;
    }
  | null
  | undefined;

/** Only the first pageload auth bootstrap should emit startup.* spans (CRE-281). */
let startupTracingEnabled = false;

/** Called once from AuthStore on first onAuthStateChanged — not a public re-arm API. */
export function enableStartupTracing(): void {
  startupTracingEnabled = true;
}

/** End pageload startup tracing after redirect or session fetch (CRE-281). Safe to call multiple times. */
export function finishStartupTracing(): void {
  startupTracingEnabled = false;
}

/** True while App auth gate is still resolving canUseApp / org membership (CRE-281). */
export function isAuthBootstrapPending(user: AuthBootstrapUser): boolean {
  return (
    user === undefined ||
    (user !== null && user.canUseApp === undefined) ||
    (user !== null && user.hasOrganization === undefined)
  );
}

/** Session deep-links defer tracing finish until SessionsStore.watchCurrentSession (CRE-281). */
export function isSessionStartupRoute(routeName: RouteNames | undefined): boolean {
  if (!routeName) {
    return false;
  }
  return (
    routeName.startsWith('session-') ||
    routeName === RouteNames.CARD ||
    routeName === RouteNames.CARD_LEGACY
  );
}

/**
 * [TRACING-TERMINAL] Close pageload startup tracing once auth bootstrap completes.
 * Centralized in App.tsx — session routes still finish in sessions.ts after fetch.
 */
export function useAppStartupTracingFinish(
  user: AuthBootstrapUser,
  currentRoute: RouteNames | undefined,
  shouldAlwaysShowRouter: boolean,
): void {
  useEffect(() => {
    if (isAuthBootstrapPending(user)) {
      return;
    }

    if (!shouldAlwaysShowRouter) {
      if (user && !user.canUseApp && !user.isGuest) {
        finishStartupTracing();
        return;
      }
      if (user && user.hasPendingInvitation) {
        finishStartupTracing();
        return;
      }
      if (user && !user.hasOrganization && !user.isGuest) {
        finishStartupTracing();
        return;
      }
    }

    if (isSessionStartupRoute(currentRoute)) {
      return;
    }

    finishStartupTracing();
  }, [user, currentRoute, shouldAlwaysShowRouter]);
}

/**
 * Child span on the pageload/navigation transaction.
 *
 * We intentionally do NOT call scope.setSpan() — the v6 Hub scope is global and
 * auth bootstrap + session fetch can run concurrently on direct session URLs,
 * which would corrupt nested span parentage (CRE-281).
 */
function startStartupSpan(op: string, description: string, data?: SpanData) {
  const transaction = getActiveTransaction();
  return transaction?.startChild({ op, description, data });
}

function applySpanData(span: ReturnType<typeof startStartupSpan>, data: SpanData): void {
  if (!span) return;
  Object.entries(data).forEach(([key, value]) => {
    span.setData(key, value);
  });
}

/** Time an async bootstrap step so Sentry can attribute root-route latency (CRE-281). */
export async function withStartupSpan<T>(
  op: string,
  description: string,
  fn: () => Promise<T>,
  data?: SpanData | ((result: T) => SpanData),
): Promise<T> {
  if (!startupTracingEnabled) {
    return fn();
  }

  const initialData = typeof data === 'function' ? undefined : data;
  const span = startStartupSpan(op, description, initialData);
  try {
    const result = await fn();
    if (typeof data === 'function') {
      applySpanData(span, data(result));
    }
    return result;
  } catch (error) {
    span?.setStatus('internal_error');
    throw error;
  } finally {
    span?.finish();
  }
}

/** Time a sync bootstrap step (e.g. redirect target selection). */
export function withStartupSpanSync<T>(
  op: string,
  description: string,
  fn: () => T,
  data?: SpanData,
): T {
  if (!startupTracingEnabled) {
    return fn();
  }

  const span = startStartupSpan(op, description, data);
  try {
    return fn();
  } catch (error) {
    span?.setStatus('internal_error');
    throw error;
  } finally {
    span?.finish();
  }
}
